import { Suspense, lazy, useEffect, useMemo, useState } from "react";

import Link from "next/link";
import { useRouter } from "next/router";

import { motion } from "framer-motion";
import { FiSliders } from "react-icons/fi";
import { RiTrophyFill } from "react-icons/ri";

import useGroupContext from "pages/[tenant]/g/hooks/useGroupContext";

import ChevronRight from "shared/assets/icons/ChevronRight";
import CircularLoader from "shared/components/atoms/CircularLoader";
import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import { useIOSLimitations } from "shared/hooks/useIOSLimitations";
import { useOrganizationConfig } from "shared/hooks/useOrganizationConfig";
import { ActiveFeedIcon } from "shared/providers/Layout/components/NewTopBar/assets/ActiveFeedIcon";
import { ConfigIcon } from "shared/providers/Layout/components/NewTopBar/assets/ConfigIcon";
import { HomeIcon } from "shared/providers/Layout/components/NewTopBar/assets/HomeIcon";
import { LogoutIcon } from "shared/providers/Layout/components/NewTopBar/assets/LogoutIcon";
import { MenuIcon } from "shared/providers/Layout/components/NewTopBar/assets/MenuIcon";
import { MobileFeedIcon } from "shared/providers/Layout/components/NewTopBar/assets/MobileFeedIcon";
import { NotificationsIcon } from "shared/providers/Layout/components/NewTopBar/assets/NotificationsIcon";
import { SearchIcon } from "shared/providers/Layout/components/NewTopBar/assets/SearchIcon";
import { UserIcon } from "shared/providers/Layout/components/NewTopBar/assets/UserIcon";
import CustomButtonCTA from "shared/providers/Layout/components/NewTopBar/components/atoms/CustomButtonCTA";
import { MenusList } from "shared/providers/Layout/components/NewTopBar/components/atoms/MenusList";
import { Notifications } from "shared/providers/Layout/components/NewTopBar/components/atoms/Notifications";
import { SwitchTheme } from "shared/providers/Layout/components/NewTopBar/components/atoms/SwitchTheme";
import FooterMenuMyGroupList from "shared/providers/Layout/components/NewTopBar/components/molecules/FooterMenuMyGroupList";
import { Search } from "shared/providers/Layout/components/NewTopBar/components/molecules/Search";
import { useMenusContext } from "shared/providers/Layout/components/NewTopBar/hooks/useMenusContext";
import { useTopbarContext } from "shared/providers/Layout/hooks/useTopbarContext";
import componentLoader from "shared/utils/componentLoader";
import { echo } from "shared/utils/echo";
import { getGamificationCoinIcon } from "shared/utils/getGamificationCoinIcon";
import { useAuthContext } from "shared/utils/hooks/useAuthContext";
import { useFeature } from "shared/utils/hooks/useFeature";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";
import useMainPage from "shared/utils/hooks/useMainPage";
import useWindowSize from "shared/utils/hooks/useWindowSize";

import { hasRequiredPermissions } from "@Console/utils/checkHasPermission";
import { permissionsAccessAdminConsole } from "@Console/utils/permissionsAreasAdminConsole";

import { ExpStarIcon } from "../../../assets/ExpStarIcon";
import MyPanelIcon from "../../../assets/MyPanelIcon";
import GamificationMenuInfos from "../../atoms/GamificationMenuInfos";
import {
  ContentItem,
  FullViewMenu,
  MobileFooterNavigationContainer,
  PointsGainedSection,
} from "./styles";

const OrganizationSelect = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkFilename: "OrganizationSelect", filename: "OrganizationSelect" */
        "shared/providers/Layout/components/NewTopBar/components/molecules/OrganizationSelect"
      ),
  ),
);

export const MobileFooterNavigation = () => {
  const { shouldAllowUserChangeOrganization } = useOrganizationConfig();
  const {
    loggedUser: user,
    signOut,
    rolesUser,
    userCoins,
    setUserCoins,
    userPoints,
    setUserPoints,
  } = useAuthContext();
  const { state, setState } = useGlobalContext();
  const mainPage = useMainPage();

  const userIsLogged = !!user;

  const { pathname } = useRouter();

  const {
    closeFooterMobileMenu,
    openFooterMobileMenu,
    footerMobileMenuIsOpen,
    notificationsIsOpen,
    openNotifications,
    closeNotifications,
    searchIsOpen,
    closeSearch,
    openSearch,
    isContentMenuOpen,
    closeContentMenu,
    openContentMenu,
    hasNewNotifications,
    closeAllFooterMobileOptions,
  } = useTopbarContext();
  const { shouldEnableIOSLimitations } = useIOSLimitations();
  const { handleFetchMenus, isFetchingMenus } = useMenusContext();
  const isCoProducer = user?.has_recipient && !user?.promoter;
  const isPromoter = user?.promoter;

  const handleOpenAuthModal = () => {
    closeFooterMobileMenu();
    setState((prev) => ({
      ...prev,
      modal: {
        open: true,
        page: "login",
      },
    }));
  };

  const isCheckoutPage = pathname.includes("/payment");
  const isMainPage =
    pathname.includes(`/${mainPage}`) &&
    !(
      searchIsOpen ||
      notificationsIsOpen ||
      footerMobileMenuIsOpen ||
      pathname.includes("/feed")
    );
  const isFeedPage =
    pathname.includes("/feed") &&
    !(
      searchIsOpen ||
      notificationsIsOpen ||
      footerMobileMenuIsOpen ||
      isMainPage
    );

  const isSearchEnabled = useFeature("f_search");
  const isNotificationsEnabled = useFeature("f_user_notifications");
  const isGlobalGamificationEnabled = useFeature("f_global_gamification");
  const isStudentProfileEnabled = useFeature("f_user_profile");
  const isFeedEnabled = useFeature("f_feed");
  const isFeedConfigActive =
    state?.tenancy?.settings?.["social-network"]?.feedActive == 1;

  useEffect(() => {
    if (!!user && !userCoins && !userPoints) {
      setUserCoins(user?.total_coins);
      setUserPoints(user?.total_points);
    }
  }, [userCoins, userPoints]);

  const [showCoins, setShowCoins] = useState(false);
  const [showPoints, setShowPoints] = useState(false);

  const [addedCoins, setAddedCoins] = useState(null);
  const [addedPoints, setAddedPoints] = useState(null);

  const userID = user?.id;

  useEffect(() => {
    const tenantUuid = state?.tenancy?.uuid;

    echo
      ?.channel(`gamification-triggers.${tenantUuid}`)
      ?.listen("TriggerNotification", (data) => {
        if (data?.activity?.user_id === userID) {
          if (data?.activity?.coins) {
            setShowCoins(true);
            setAddedCoins(data?.activity?.coins);
            setUserCoins(userCoins + parseInt(data?.activity?.coins));
            setTimeout(() => {
              setShowCoins(false);
              setAddedCoins(null);
            }, 2000);
          }

          if (data?.activity?.points) {
            setShowPoints(true);
            setAddedPoints(data?.activity?.points);
            setUserPoints(userPoints + parseInt(data?.activity?.points));
            setTimeout(() => {
              setShowPoints(false);
              setAddedPoints(null);
            }, 2000);
          }
        }
      });

    return () => {
      if (echo) {
        echo.leaveChannel(`gamification-triggers.${tenantUuid}`);
      }
    };
  }, [echo, userCoins, userPoints]);

  const hasPermissionAdminConsole = hasRequiredPermissions(
    rolesUser,
    permissionsAccessAdminConsole,
  );

  const gamificationData = state?.tenancy?.settings?.gamification;

  const isRankingEnabled = gamificationData?.enableGlobalRanking == 1;
  const isGamificationEnabled = gamificationData?.enableGamification == 1;

  const coinsColor = gamificationData?.coinsColor;
  const coinIcon = getGamificationCoinIcon(gamificationData?.coinsIcon);

  const { fetchMyGroups } = useGroupContext();

  const handleClickMenu = async () => {
    if (footerMobileMenuIsOpen) {
      closeFooterMobileMenu();
    } else {
      openFooterMobileMenu();
      await Promise.allSettled([fetchMyGroups(), handleFetchMenus()]);
    }
  };

  const { width } = useWindowSize();
  const isMobile = width <= 850;

  const gridColunmsToAdd = useMemo(() => {
    let result = 0;

    if (isSearchEnabled) result++;
    if (isFeedEnabled && isFeedConfigActive) result++;

    return result;
  }, [isSearchEnabled, isFeedEnabled, isFeedConfigActive]);

  if (!isMobile) return null;
  if (isCheckoutPage) return null;

  return (
    <>
      <PointsGainedSection coinsColor={coinsColor}>
        {showCoins && (
          <motion.div
            className="gained-coins"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: [0, 1, 0], y: [-10, -50] }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 2 }}
          >
            {coinIcon[0]?.icon}
            <p>+ {addedCoins}</p>
          </motion.div>
        )}

        {showPoints && (
          <motion.div
            className="gained-exp"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: [0, 1, 0], y: [-10, -50] }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 2 }}
          >
            <ExpStarIcon />
            <p>+ {addedPoints}</p>
          </motion.div>
        )}
      </PointsGainedSection>
      <MobileFooterNavigationContainer
        coinsColor={coinsColor}
        hasNewNotifications={hasNewNotifications}
        userIsLogged={userIsLogged}
        gridColunmsToAdd={gridColunmsToAdd}
        shouldEnableIOSLimitations={shouldEnableIOSLimitations}
      >
        <ul>
          <li>
            <Link
              href={`/${mainPage}`}
              onClick={closeAllFooterMobileOptions}
              prefetch={false}
              className={`${isMainPage && "active"}`}
            >
              <HomeIcon />
            </Link>
          </li>
          {isSearchEnabled && (
            <li>
              <button
                onClick={searchIsOpen ? closeSearch : openSearch}
                className={`${searchIsOpen && "active"}`}
              >
                <SearchIcon />
              </button>
            </li>
          )}

          {userIsLogged &&
            isFeedEnabled &&
            isFeedConfigActive &&
            !shouldEnableIOSLimitations && (
              <li>
                <Link
                  href={"/feed"}
                  prefetch={false}
                  className={`${isFeedPage && "active"}`}
                  onClick={closeAllFooterMobileOptions}
                >
                  {isFeedPage ? <ActiveFeedIcon /> : <MobileFeedIcon />}
                </Link>
              </li>
            )}

          {userIsLogged && (
            <li>
              <button
                onClick={
                  notificationsIsOpen ? closeNotifications : openNotifications
                }
                className={`${
                  notificationsIsOpen && "active"
                } notification-button`}
                disabled={!userIsLogged || !isNotificationsEnabled}
              >
                <NotificationsIcon />
              </button>
            </li>
          )}

          <li>
            <button
              onClick={handleClickMenu}
              className={`${footerMobileMenuIsOpen && "active"}`}
            >
              <MenuIcon />
            </button>
          </li>

          {!userIsLogged && (
            <li>
              <button onClick={handleOpenAuthModal}>
                <UserIcon />
              </button>
            </li>
          )}
        </ul>
      </MobileFooterNavigationContainer>

      <FullViewMenu show={footerMobileMenuIsOpen}>
        {shouldAllowUserChangeOrganization && (
          <ComponentErrorBoundary>
            <Suspense fallback={<CircularLoader className="loader" />}>
              <OrganizationSelect
                onChangeOrganization={closeFooterMobileMenu}
              />
            </Suspense>
          </ComponentErrorBoundary>
        )}

        {userIsLogged && !shouldEnableIOSLimitations && (
          <div className="user-info">
            {isGlobalGamificationEnabled && isGamificationEnabled && (
              <GamificationMenuInfos />
            )}
          </div>
        )}

        {isFetchingMenus ? (
          <CircularLoader className="loader" />
        ) : (
          !shouldEnableIOSLimitations && (
            <MenusList
              rightArrowIcon
              triggerOnLinkClick={closeFooterMobileMenu}
              shouldChangeTopbarSidebarMenuButtonTextOnOpen={false}
            />
          )
        )}

        {!shouldEnableIOSLimitations && (
          <div className="CTA-container">
            <CustomButtonCTA triggerOnLinkClick={closeFooterMobileMenu} />
          </div>
        )}

        {userIsLogged && (
          <>
            <ContentItem
              onClick={isContentMenuOpen ? closeContentMenu : openContentMenu}
            >
              Meus conteúdos
              <ChevronRight />
            </ContentItem>

            {isStudentProfileEnabled && (
              <Link
                onClick={closeFooterMobileMenu}
                href="/foryou"
                prefetch={false}
              >
                Meu painel <MyPanelIcon />
              </Link>
            )}
          </>
        )}

        {(hasPermissionAdminConsole || isCoProducer) &&
          !shouldEnableIOSLimitations && (
            <Link
              href="/console"
              onClick={closeFooterMobileMenu}
              prefetch={false}
            >
              Admin Console <ConfigIcon />
            </Link>
          )}

        {isPromoter && !shouldEnableIOSLimitations && (
          <Link
            href="/cp/analytics"
            onClick={closeFooterMobileMenu}
            prefetch={false}
          >
            Painel de controle
            <ConfigIcon />
          </Link>
        )}

        {isGlobalGamificationEnabled &&
          userIsLogged &&
          isRankingEnabled &&
          !shouldEnableIOSLimitations && (
            <Link
              href="/ranking"
              onClick={closeFooterMobileMenu}
              prefetch={false}
            >
              Ranking global <RiTrophyFill />
            </Link>
          )}

        <SwitchTheme />

        {userIsLogged && (
          <Link
            href="/account"
            onClick={closeFooterMobileMenu}
            prefetch={false}
          >
            Minha conta <FiSliders />
          </Link>
        )}

        {userIsLogged ? (
          <button onClick={signOut}>
            Sair da conta <LogoutIcon />
          </button>
        ) : (
          <button onClick={handleOpenAuthModal}>
            Entrar / Criar conta <UserIcon />
          </button>
        )}
      </FullViewMenu>

      <FooterMenuMyGroupList />

      {isNotificationsEnabled && <Notifications />}

      <Search />
    </>
  );
};
